import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./i18n";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const UserManagement = () => {
  const { API, token } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', full_name: '', email: '', role: 'user' });
  const [loading, setLoading] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(response.data);
    } catch (error) { toast.error(t('errorLoadingData')); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) { toast.error(t('pleaseEnterDeaconName')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/users/create`, newUser, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('userCreatedSuccessfully'));
      setIsCreateUserOpen(false);
      setNewUser({ username: '', password: '', full_name: '', email: '', role: 'user' });
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); } finally { setLoading(false); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API}/api/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('userDeletedSuccessfully'));
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await axios.put(`${API}/api/users/${userId}/toggle-status`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('userStatusUpdated'));
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  return (
    <Card className="shadow-lg border-purple-200">
      <CardHeader className="bg-purple-100">
        <CardTitle className="flex items-center gap-2 text-purple-900"><UserPlus className="w-5 h-5" />{t('userManagement')}</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <Button onClick={() => setIsCreateUserOpen(true)} className="w-full bg-purple-600 hover:bg-purple-700 mb-4"><UserPlus className="w-4 h-4 mr-2" />{t('createUser')}</Button>
        <div className="space-y-3">
          {users.map((user) => (
            <div key={user.id} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border">
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{user.full_name || user.username}</p>
                <p className="text-sm text-gray-600">@{user.username}</p>
                <div className="flex gap-2 mt-1">
                  <Badge className={user.role === 'admin' ? 'bg-amber-600' : 'bg-green-600'}>{user.role === 'admin' ? t('admin') : t('user')}</Badge>
                  <Badge className={user.is_active ? 'bg-green-600' : 'bg-gray-600'}>{user.is_active ? t('enabled') : t('disabled')}</Badge>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleToggleStatus(user.id)} className="border-blue-300 text-blue-700">{user.is_active ? t('disable') : t('enable')}</Button>
                <Button size="sm" variant="outline" onClick={() => handleDeleteUser(user.id)} className="border-red-300 text-red-700"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
        <Dialog open={isCreateUserOpen} onOpenChange={setIsCreateUserOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('createUser')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium mb-2">{t('username')} *</label><Input value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} placeholder={t('username')} /></div>
              <div><label className="block text-sm font-medium mb-2">{t('password')} *</label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder={t('password')} /></div>
              <div><label className="block text-sm font-medium mb-2">{t('fullName')}</label><Input value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} placeholder={`${t('fullName')} (${t('optional')})`} /></div>
              <div><label className="block text-sm font-medium mb-2">{t('email')}</label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder={`${t('email')} (${t('optional')})`} /></div>
              <div><label className="block text-sm font-medium mb-2">{t('permissions')}</label>
                <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="user">{t('normalUser')}</SelectItem><SelectItem value="admin">{t('admin')}</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateUser} disabled={loading} className="flex-1 bg-purple-600 hover:bg-purple-700">{loading ? t('creating') : t('register')}</Button>
                <Button onClick={() => setIsCreateUserOpen(false)} variant="outline" className="flex-1">{t('cancel')}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UserManagement;
